/**
 * Map filter definitions — predicate-based building filters for the city map.
 * Each filter highlights buildings matching a condition (buyable, rentable, etc.)
 * and dims everything else.
 *
 * Performance: matching node sets are pre-computed on filter change,
 * not on every frame. The render loop does O(1) Map.get() lookups.
 */

import { t } from '../i18n';

export type FilterId = 'buyable' | 'rentable' | 'income' | 'extortable' | 'recruit' | 'hq_slot' | 'storage';

export interface WorldBuildingLike {
  node_id: string;
  buy_price: number;
  income_base: number;
  illegal: boolean;
  rentable: boolean;
  extort_rate: [number, number];
  is_hq_slot: boolean;
  storage_cap: number;
  recruits_tag?: boolean;
}

export interface MapFilter {
  id: FilterId;
  label: string;
  color: string;
  /** Action ID to pre-fill in plan overlay when this filter is active */
  planAction?: string;
  match(bldg: WorldBuildingLike, ownedNodeIds: Set<string>): boolean;
}

// Labels use t() — evaluated lazily at access time via get label()
export const FILTERS: MapFilter[] = [
  {
    id: 'buyable', get label() { return t('map.filter_buyable'); }, color: '#06d6a0', planAction: 'BUY_BUILDING',
    match: (b, owned) => b.buy_price > 0 && !owned.has(b.node_id),
  },
  {
    id: 'rentable', get label() { return t('map.filter_rentable'); }, color: '#4361ee', planAction: 'RENT_BUILDING',
    match: (b, owned) => b.rentable && !owned.has(b.node_id),
  },
  {
    id: 'income', get label() { return t('map.filter_income'); }, color: '#00f5d4',
    match: (b, owned) => b.income_base > 0 && !owned.has(b.node_id) && !b.illegal,
  },
  {
    id: 'extortable', get label() { return t('map.filter_extortion'); }, color: '#fca311', planAction: 'EXTORT',
    match: (b, owned) => b.extort_rate[1] > 0 && !owned.has(b.node_id),
  },
  {
    id: 'recruit', get label() { return t('map.filter_recruits'); }, color: '#9b5de5', planAction: 'RECRUIT',
    match: (b) => b.recruits_tag === true,
  },
  {
    id: 'hq_slot', get label() { return t('map.filter_hq_slot'); }, color: '#f7d060',
    match: (b) => b.is_hq_slot,
  },
  {
    id: 'storage', get label() { return t('map.filter_storage'); }, color: '#ff8c42',
    match: (b) => b.storage_cap > 0,
  },
];

// ── State ────────────────────────────────────────────────────────────────────

export const activeFilters = new Set<FilterId>();

/** Pre-computed: nodeId → first matching FilterId. Updated by recompute(). */
let matchingNodes = new Map<string, FilterId>();
let matchCount = 0;
let matchSectors = 0;

/** Callback for map to know when to redraw */
let onChangeCallback: (() => void) | null = null;

export function onFilterChange(cb: () => void): void {
  onChangeCallback = cb;
}

export function toggleFilter(id: FilterId): void {
  if (activeFilters.has(id)) {
    activeFilters.delete(id);
  } else {
    activeFilters.add(id);
  }
  // Don't recompute here — caller must call recompute() with fresh building data
  onChangeCallback?.();
}

export function isAnyFilterActive(): boolean {
  return activeFilters.size > 0;
}

/**
 * Recompute the matching node set from current filters and building data.
 * Call this when filters change or when building data is loaded/updated.
 */
export function recompute(
  buildings: Iterable<WorldBuildingLike>,
  ownedNodeIds: Set<string>,
): void {
  matchingNodes = new Map();

  for (const b of buildings) {
    for (const fid of activeFilters) {
      const f = FILTERS.find(f => f.id === fid);
      if (f && f.match(b, ownedNodeIds)) {
        matchingNodes.set(b.node_id, fid);
        // sector_id not available on WorldBuildingLike — tracked via map.ts
        break; // first matching filter wins for color
      }
    }
  }

  matchCount = matchingNodes.size;
  // matchSectors computed by caller (map.ts has sector info)
}

/** O(1) lookup: does this node match any active filter? Returns FilterId or undefined. */
export function getNodeFilter(nodeId: string): FilterId | undefined {
  return matchingNodes.get(nodeId);
}

/** Get the filter color for a FilterId */
export function getFilterColor(id: FilterId): string {
  return FILTERS.find(f => f.id === id)?.color ?? '#ffffff';
}

/** Get the plan action for a FilterId */
export function getFilterPlanAction(id: FilterId): string | undefined {
  return FILTERS.find(f => f.id === id)?.planAction;
}

export function getMatchCount(): number { return matchCount; }

/** Count matching sectors (call after recompute with sector data) */
export function setMatchSectors(n: number): void { matchSectors = n; }
export function getMatchSectors(): number { return matchSectors; }
